import React, { useState, useEffect, useCallback } from 'react';
import { getDoc, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import { fetchInventoryItems, addItemsToTrade, completeTrade, rejectTrade, cancelTrade } from './TradeFunctions';
import { FaArrowLeft, FaCheck, FaTimes, FaBan } from 'react-icons/fa';
import './Trade.css';
import { debounce } from '../utils/DebounceUtils';

const Trade = () => {
  const { user } = useFireAuth();
  const navigate = useNavigate();
  const { tradeId } = useParams();
  const [friend, setFriend] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [fromItems, setFromItems] = useState([]);
  const [toItems, setToItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tradeCompleted, setTradeCompleted] = useState(false);
  const [message, setMessage] = useState('');

  const fetchItemDetails = async (itemIds) => {
    const itemDetails = await Promise.all(
      itemIds.map(async (itemId) => {
        const originalItemId = itemId.split('_').pop();
        const itemDoc = await getDoc(doc(db, 'items', originalItemId));
        if (itemDoc.exists()) {
          return { id: itemId, ...itemDoc.data() };
        }
        return null;
      })
    );
    return itemDetails.filter(item => item !== null);
  };

  const fetchTradeDetails = useCallback(async () => {
    if (tradeId) {
      try {
        const unsubscribe = onSnapshot(doc(db, 'trades', tradeId), async (tradeDoc) => {
          if (tradeDoc.exists()) {
            const tradeData = tradeDoc.data();
            setTradeDetails({ id: tradeId, ...tradeData });
            const friendId = tradeData.from === user.uid ? tradeData.to : tradeData.from;
            const friendDoc = await getDoc(doc(db, 'users', friendId));
            if (friendDoc.exists()) {
              setFriend({ id: friendDoc.id, ...friendDoc.data() });
            }

            const fromItemsDetails = await fetchItemDetails(tradeData.fromItems || []);
            const toItemsDetails = await fetchItemDetails(tradeData.toItems || []);
            setFromItems(fromItemsDetails);
            setToItems(toItemsDetails);

            if (tradeData.status === 'completed') {
              setTradeCompleted(true);
            } else if (tradeData.status === 'cancelled') {
              setMessage('Trade has been cancelled.');
              setTimeout(() => navigate('/portal/trades'), 2000);
            }
          } else {
            console.error('Trade not found');
            setTradeDetails(null);
          }
        });
        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching trade details:', error);
        setTradeDetails(null);
      }
    }
  }, [tradeId, user.uid, navigate]);

  const fetchInventory = useCallback(async () => {
    if (user && user.uid) {
      try {
        const items = await fetchInventoryItems(user.uid);
        setInventoryItems(items);
      } catch (error) {
        console.error('Error fetching inventory items:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    const loadData = async () => {
      await fetchTradeDetails();
      await fetchInventory();
      setIsLoading(false);
    };
    loadData();
  }, [fetchTradeDetails, fetchInventory]);

  const handleSelectItem = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleProposeTrade = debounce(async () => {
    if (selectedItems.length === 0) return;
    const success = await addItemsToTrade(tradeId, user.uid, selectedItems);
    if (success) {
      setSelectedItems([]);
      setMessage('Trade offered successfully.');
      await fetchInventory();
    } else {
      setMessage('Failed to propose trade. Please try again.');
    }
  }, 500);

  const handleCompleteTrade = debounce(async () => {
    if (!tradeDetails) return;
    const success = await completeTrade(tradeDetails.id);
    if (success) {
      setTradeCompleted(true);
      setMessage('Trade completed successfully.');
      await fetchInventory();
      setTimeout(() => navigate('/portal/trades'), 2000);
    } else {
      setMessage('Failed to complete trade. Please try again.');
    }
  }, 500);

  const handleRejectTrade = debounce(async () => {
    if (!tradeDetails) return;
    const success = await rejectTrade(tradeDetails.id);
    if (success) {
      setMessage('Trade rejected and cancelled.');
      setTimeout(() => navigate('/portal/trades'), 2000);
    } else {
      setMessage('Failed to reject trade. Please try again.');
    }
  }, 500);

  const handleCancelTrade = debounce(async () => {
    if (!tradeDetails) return;
    const result = await cancelTrade(tradeDetails.id, user.uid);
    if (result.success) {
      setMessage('Trade cancelled successfully.');
      setTimeout(() => navigate('/portal/trades'), 2000);
    } else {
      setMessage(result.message || 'Failed to cancel trade. Please try again.');
    }
  }, 500);

  const renderItems = (items) => (
    items && items.length > 0 ? (
      <div className="trade-pending-grid">
        {items.map(item => (
          <div key={item.id} className="trade-pending-item">
            <div className="trade-pending-item-image-container">
              <img src={item.imageUrl} alt={item.name} className="trade-pending-item-image" />
            </div>
            <div className="trade-pending-item-details">
              <p className="trade-pending-item-name">{item.name}</p>
              <p className="trade-pending-item-value">{item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p>No items selected</p>
    )
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-container">
      <div className="trade-page">
        <FaArrowLeft className="trade-back-arrow" onClick={() => navigate('/portal/trades')} />
        <div className="trade-container">
          {tradeDetails ? (
            <>
              <h2>Trade with {friend?.name || 'Friend'}</h2>
              {message && <p className="trade-message">{message}</p>}
              {!tradeCompleted ? (
                <div className="trade-details">
                  {tradeDetails?.status === 'fromselect' && tradeDetails.from === user.uid && (
                    <div className="trade-card inventory">
                      <h3>Your Inventory</h3>
                      <div className="trade-inventory-grid">
                        {inventoryItems.map((item) => (
                          <div
                            key={item.id}
                            className={`trade-inventory-item ${selectedItems.includes(item) ? 'selected' : ''}`}
                            onClick={() => handleSelectItem(item)}
                          >
                            <div className="trade-item-image-container">
                              <img src={item.imageUrl} alt={item.name} className="trade-item-image" />
                            </div>
                            <div className="trade-item-details">
                              <p className="trade-item-name">{item.name}</p>
                              <p className="trade-item-value">{item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="trade-selected-items">
                        <h3>Your Offer</h3>
                        <div className="selected-items-list">
                          {selectedItems.map(item => (
                            <div key={item.id} className="trade-selected-item">
                              <img src={item.imageUrl} alt={item.name} />
                              <p>{item.name}</p>
                            </div>
                          ))}
                        </div>
                        <button className="trade-action-button" onClick={handleProposeTrade}>
                          <FaCheck /> Propose Trade
                        </button>
                      </div>
                    </div>
                  )}
                  {tradeDetails?.status === 'toselect' && (
                    <div className="trade-card">
                      {tradeDetails.to === user.uid ? (
                        <>
                          <div className="trade-inventory-section">
                            <h3>Your Inventory</h3>
                            <div className="trade-inventory-grid">
                              {inventoryItems.map((item) => (
                                <div
                                  key={item.id}
                                  className={`trade-inventory-item ${selectedItems.includes(item) ? 'selected' : ''}`}
                                  onClick={() => handleSelectItem(item)}
                                >
                                  <div className="trade-item-image-container">
                                    <img src={item.imageUrl} alt={item.name} className="trade-item-image" />
                                  </div>
                                  <div className="trade-item-details">
                                    <p className="trade-item-name">{item.name}</p>
                                    <p className="trade-item-value">{item.currency === 'money' ? `💴 ${item.price}` : `🪙 ${item.price}`}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="trade-selected-items">
                            <h3>Your Offer</h3>
                            <div className="selected-items-list">
                              {selectedItems.map(item => (
                                <div key={item.id} className="trade-selected-item">
                                  <img src={item.imageUrl} alt={item.name} />
                                  <p>{item.name}</p>
                                </div>
                              ))}
                            </div>
                            <button className="trade-action-button" onClick={handleProposeTrade}>
                              <FaCheck /> Propose Trade
                            </button>
                          </div>
                          <div className="trade-card">
                            <h3>{friend?.name || 'Friend'}'s Offer</h3>
                            <div className="trade-items">
                              {renderItems(fromItems)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <p>Waiting for {friend?.name || 'Friend'} to select items</p>
                          <h4>Your Offer</h4>
                          <div className="trade-items">
                            {renderItems(fromItems)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {tradeDetails?.status === 'pending' && (
                    <div className="trade-card">
                      <h3>Trade Details</h3>
                      <div className="trade-items">
                        <div className="trade-from">
                          <h4>{tradeDetails.from === user.uid ? 'Your Offer' : `${friend?.name || 'Friend'}'s Offer`}</h4>
                          {renderItems(fromItems)}
                        </div>
                        <div className="trade-to">
                          <h4>{tradeDetails.to === user.uid ? 'Your Offer' : `${friend?.name || 'Friend'}'s Offer`}</h4>
                          {renderItems(toItems)}
                        </div>
                      </div>
                      {tradeDetails.from === user.uid && (
                        <div className="trade-actions">
                          <button className="trade-action-button" onClick={handleCompleteTrade}>
                            <FaCheck /> Complete Trade
                          </button>
                          <button className="trade-action-button" onClick={handleRejectTrade}>
                            <FaTimes /> Reject
                          </button>
                        </div>
                      )}
                      {tradeDetails.to === user.uid && (
                        <p>Waiting for {friend?.name || 'Friend'} to accept or reject the trade</p>
                      )}
                    </div>
                  )}
                  {tradeDetails?.status === 'fromselect' && tradeDetails.to === user.uid && (
                    <p>Waiting for {friend?.name || 'Friend'} to select items</p>
                  )}
                  {tradeDetails?.status !== 'completed' && tradeDetails?.status !== 'cancelled' && (
                    <div className="trade-actions">
                      <button className="trade-action-button" onClick={handleCancelTrade}>
                        <FaBan /> Cancel Trade
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="trade-card trade-completed">
                  <h3>Trade Completed</h3>
                  <div className="trade-items">
                    <div className="trade-from">
                      <h4>You Received:</h4>
                      {renderItems(tradeDetails.from === user.uid ? toItems : fromItems)}
                    </div>
                    <div className="trade-to">
                      <h4>You Gave:</h4>
                      {renderItems(tradeDetails.from === user.uid ? fromItems : toItems)}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>Trade not found or still loading...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelayRender(Trade, 200);
